import axios, { AxiosResponse } from "axios";

import { MKS_BACKEND_URL } from "src/utils/env";
import { GlobalUtils } from "src/utils/globalUtils";
import {
  STATUS_APPROVED,
  STATUS_REJECTED
} from "src/common/constants/statusConstants";

enum Endpoints {
  admin = "admin",
  getApplications = "get-applications",
  processapplication = "process-application",
  userActionHistory = "user-action-history",
  create = "create",
  list = "list",
  updateStatus = "update-status",
}

export class AdminService {
  private static readonly BASE_URL = MKS_BACKEND_URL;

  // Fetch user applications with Authorization header
  public static getUserApplications = async (): Promise<AxiosResponse> => {
    return axios.get([AdminService.BASE_URL, Endpoints.admin, Endpoints.getApplications].join("/"));
  };

  // Approve/Reject applications with Authorization header (now expects action: "Approved" | "Rejected")
  public static approveUserApplication = async (
    emailIds: string,
    action: typeof STATUS_APPROVED | typeof STATUS_REJECTED,
    reason?: string
  ): Promise<AxiosResponse> => {
    const url = [AdminService.BASE_URL, Endpoints.admin, Endpoints.processapplication].join("/");
    const payload: any = { emailIds, action };

    const accountInfo = GlobalUtils.getUserLoginInfo?.();
    const approvedBy = accountInfo?.emailId;
    if (approvedBy) payload.approvedBy = approvedBy;

    if (reason) payload.reason = reason;
    // Always include decision timestamp for auditing
    payload.timestamp = new Date().toISOString();

    return axios.post(url, payload);
  };

  // Fetch user action history with Authorization header
  public static getUserActionHistory = async (
    status?: "ALL" | typeof STATUS_APPROVED | typeof STATUS_REJECTED
  ): Promise<AxiosResponse> => {
    const url = [
      AdminService.BASE_URL,
      Endpoints.admin,
      Endpoints.userActionHistory,
    ].join("/");
    const finalUrl = !status || status === "ALL"
      ? url
      : `${url}?status=${encodeURIComponent(status)}`;
    return axios.get(finalUrl);
  };

  // Convenience helpers for counts
  public static getApprovedCount = async (): Promise<number> => {
    const res = await AdminService.getUserActionHistory(STATUS_APPROVED);
    const data = Array.isArray(res.data) ? res.data : [res.data];
    return data.length;
  };

  public static getRejectedCount = async (): Promise<number> => {
    const res = await AdminService.getUserActionHistory(STATUS_REJECTED);
    const data = Array.isArray(res.data) ? res.data : [res.data];
    return data.length;
  };

  public static createAdmin = async (
    email: string,
    mobileNo?: string
  ): Promise<AxiosResponse> => {
    const url = [AdminService.BASE_URL, Endpoints.admin, Endpoints.create].join(
      "/"
    );
    const payload = { email, mobileNo };
    return axios.post(url, payload);
  };

  public static deleteAdmin = async (email: string): Promise<AxiosResponse> => {
    const url = [AdminService.BASE_URL, Endpoints.admin, "delete", email].join(
      "/"
    );
    return axios.delete(url);
  };

  public static getAdmins = async (): Promise<AxiosResponse> => {
    const url = [AdminService.BASE_URL, Endpoints.admin].join("/");
    return axios.get(url);
  };

  public static updateAdminStatus = async (
    email: string,
    status: "APPROVED" | "REJECTED" // updated to match backend
  ): Promise<AxiosResponse> => {
    const url = [
      AdminService.BASE_URL,
      Endpoints.admin,
      Endpoints.updateStatus,
      email,
      status,
    ].join("/");
    return axios.get(url);
  };

  // ── Marriage Consultant APIs ──────────────────────────────────────

  public static createConsultant = async (payload: {
    email: string;
    fullName?: string;
    mobileNo: string;
    approved: boolean;
    dailyRequestCountLimit: number;
    marriage_profile_limit: number;
  }): Promise<AxiosResponse> => {
    const token = GlobalUtils.getToken();
    const url = [AdminService.BASE_URL, Endpoints.admin, "create", "consultant"].join("/");
    return axios.post(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  public static updateConsultant = async (payload: {
    email: string;
    fullName?: string;
    mobileNo?: string;
    approved?: boolean;
    dailyRequestCountLimit?: number;
    marriage_profile_limit?: number;
  }): Promise<AxiosResponse> => {
    const token = GlobalUtils.getToken();
    const url = [AdminService.BASE_URL, Endpoints.admin, "update", "consultant"].join("/");
    return axios.post(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  /**
   * Fetch all users with ROLE_CONSULTANT.
   * GET /profile/all/ROLE_CONSULTANT
   */
  public static getConsultants = async (): Promise<AxiosResponse> => {
    const token = GlobalUtils.getToken();
    const url = [AdminService.BASE_URL, "profile", "all", "ROLE_CONSULTANT"].join("/");
    return axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };
}
